import type { CatalogIndex } from '../catalog/types'
import type { ItemRef, Quest, Computed } from './types'
import { QUALITY_MULTIPLIER } from './types'

/**
 * price(item) = truncate(saleBase × qualityMultiplier)
 *
 * Truncate, not round. Rounding is off by one per item and compounds across a
 * multi-item quest.
 */
export function unitPrice(ref: ItemRef, catalog: CatalogIndex): number | null {
  const item = catalog.items.get(ref.item)
  if (!item) return null
  return Math.trunc(item.saleBase * QUALITY_MULTIPLIER[ref.quality])
}

export interface LineValue {
  ref: ItemRef
  unit: number | null
  subtotal: number | null
  /** Unresolved catalog reference — shown as an error row, not silently zeroed. */
  missing: boolean
}

export function lineValues(refs: ItemRef[], catalog: CatalogIndex): LineValue[] {
  return refs.map((ref) => {
    const unit = unitPrice(ref, catalog)
    return {
      ref,
      unit,
      subtotal: unit === null ? null : unit * ref.amount,
      missing: unit === null,
    }
  })
}

function sum(lines: LineValue[]): number {
  return lines.reduce((total, line) => total + (line.subtotal ?? 0), 0)
}

export interface Balance {
  estimatedValue: number
  actualValue: number
  /** actual / estimated. null when estimated is 0 — common for tutorial quests. */
  ratio: number | null
  requirementLines: LineValue[]
  rewardLines: LineValue[]
  /** requirementsToShow are priced for display but excluded from estimatedValue. */
  showLines: LineValue[]
}

/**
 * estimatedValue = Σ price × amount over requirements + goldRequirement
 * actualValue    = Σ price × amount over rewards      + goldReward
 *
 * requirementsToShow and goldRequirementToShow are excluded from estimatedValue —
 * the player keeps them, so they aren't part of what the quest takes.
 *
 * Verified against the computed block of all 59 shipped quests: 0 mismatches.
 */
export function computeBalance(quest: Quest, catalog: CatalogIndex): Balance {
  const requirementLines = lineValues(quest.requirements, catalog)
  const rewardLines = lineValues(quest.rewards, catalog)
  const showLines = lineValues(quest.requirementsToShow, catalog)

  const estimatedValue = sum(requirementLines) + quest.goldRequirement
  const actualValue = sum(rewardLines) + quest.goldReward

  return {
    estimatedValue,
    actualValue,
    ratio: estimatedValue === 0 ? null : actualValue / estimatedValue,
    requirementLines,
    rewardLines,
    showLines,
  }
}

/**
 * The renown level at which the quest first becomes completable: the highest
 * unlockLevel among everything it needs, floored by renownRequirement.
 *
 * Resources carry no unlockLevel (they're gathered, not learned) and contribute 0.
 *
 * Caveat: matches all 59 shipped quests, but no current quest exercises either the
 * requirementsToShow term or a renownRequirement high enough to win the max, so
 * those two terms follow the spec rather than observed data. Unity regenerates this
 * on export, so a wrong guess self-corrects into a visible diff.
 */
export function computeGatingLevel(quest: Quest, catalog: CatalogIndex): number {
  const levels = [quest.renownRequirement]
  for (const ref of [...quest.requirements, ...quest.requirementsToShow]) {
    levels.push(catalog.items.get(ref.item)?.unlockLevel ?? 0)
  }
  return Math.max(...levels)
}

/**
 * The unlock level of each consumed craftable, comma separated, in requirement
 * order. Resources are skipped — they have no unlock level.
 *
 * No shipped quest consumes a resource, so the skip follows the spec's wording
 * ("each consumed craftable") rather than observed data.
 */
export function computeRequirementLevels(quest: Quest, catalog: CatalogIndex): string {
  const levels: number[] = []
  for (const ref of quest.requirements) {
    const item = catalog.items.get(ref.item)
    if (item?.unlockLevel !== undefined) levels.push(item.unlockLevel)
  }
  return levels.length > 0 ? levels.join(',') : 'No requirement'
}

/** The full computed block, as Unity would regenerate it on the next export. */
export function computeAll(quest: Quest, catalog: CatalogIndex): Computed {
  const balance = computeBalance(quest, catalog)
  return {
    estimatedValue: balance.estimatedValue,
    actualValue: balance.actualValue,
    gatingLevel: computeGatingLevel(quest, catalog),
    requirementLevels: computeRequirementLevels(quest, catalog),
  }
}

/** True when the file's stored computed block disagrees with a fresh calculation. */
export function computedIsStale(quest: Quest, catalog: CatalogIndex): boolean {
  const fresh = computeAll(quest, catalog)
  return (
    fresh.estimatedValue !== quest.computed.estimatedValue ||
    fresh.actualValue !== quest.computed.actualValue ||
    fresh.gatingLevel !== quest.computed.gatingLevel ||
    fresh.requirementLevels !== quest.computed.requirementLevels
  )
}
